// Standalone chrome (Nav + Footer) extracted from _v01_trustbank.jsx so it
// can be mounted on interior portal pages, not just the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#f7f4ee', fg: '#1a2236', muted: '#5a6378',
  border: '#d9d3c5', accent: '#1f3a5f', accentFg: '#f7f4ee',
  inputBg: '#ffffff', radius: 4,
  font: '"Source Serif 4", Georgia, serif',
  dark: '#0e1726', cardLight: '#ffffff',
};

const fontSerif = '"Source Serif 4", Georgia, serif';
const fontBody = '"Inter", system-ui, sans-serif';
const fontMono = '"IBM Plex Mono", ui-monospace, monospace';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Ratgeber', href: '/ratgeber' },
  { label: 'So funktioniert es', href: '/so-funktioniert-es' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Kontakt', href: '/kontakt' },
];

export function V01TrustbankNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '12px 16px' : '14px 32px',
      borderBottom: `1px solid ${theme.border}`, background: theme.bg,
      fontFamily: theme.font, color: theme.fg, position: 'relative',
    }}>
      <Link href="/" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: 'inherit', textDecoration: 'none' }}>{brand}</Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 22, fontSize: 13, color: theme.muted }}>
            {navLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </div>
          <Link href="/angebot-vergleichen" style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            fontFamily: theme.font, textDecoration: 'none', display: 'inline-block',
          }}>Angebote vergleichen</Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 14px', fontSize: 14, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6, textAlign: 'center',
            textDecoration: 'none', display: 'block', marginTop: 4,
          }}>Angebote vergleichen</Link>
        </div>
      )}
    </nav>
  );
}

export function V01TrustbankFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      borderTop: `1px solid ${theme.border}`,
      background: theme.bg,
      padding: isMobile ? '20px 16px' : '28px 56px',
      fontFamily: fontBody,
      fontSize: 12,
      color: theme.muted,
    }}>
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: 'wrap', gap: isMobile ? 12 : 16,
      }}>
        <div style={{
          fontFamily: fontSerif, fontSize: 14, color: theme.fg, fontWeight: 400,
          letterSpacing: '-0.005em',
        }}>
          {brand}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 18, flexWrap: 'wrap' }}>
          <Link href="/impressum" style={{ color: theme.muted, textDecoration: 'none' }}>Impressum</Link>
          <span style={{ color: theme.border }}>·</span>
          <Link href="/datenschutz" style={{ color: theme.muted, textDecoration: 'none' }}>Datenschutz</Link>
          <span style={{ color: theme.border }}>·</span>
          <Link href="/agb" style={{ color: theme.muted, textDecoration: 'none' }}>AGB</Link>
          <span style={{ color: theme.border }}>·</span>
          <Link href="/widerruf" style={{ color: theme.muted, textDecoration: 'none' }}>Widerruf</Link>
        </div>
        <div style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: '0.08em',
          color: theme.muted,
        }}>
          {`© ${new Date().getFullYear()} ${brand}`}
        </div>
      </div>
    </footer>
  );
}
